import _slicedToArray from "/opt/build/repo/web/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { __rest } from "tslib";
import * as React from 'react';
import { Form } from '../..';
import { useDebounce } from '../../hooks/useDebounce';
import { useThrottle } from '../../hooks/useThrottle';
import { ClassNames } from '../../utils/classNames';
import { InputWrapper } from '../inputWrapper/inputWrapper.component';
var DebounceTextAreaBase = React.forwardRef(function (_a, ref) {
  var milliseconds = _a.milliseconds,
      value = _a.value,
      onValueChange = _a.onValueChange,
      onChange = _a.onChange,
      nativeProps = __rest(_a, ["milliseconds", "value", "onValueChange", "onChange"]);

  var _useDebounce = useDebounce(milliseconds, value === null || value === void 0 ? void 0 : value.toString(), onValueChange),
      _useDebounce2 = _slicedToArray(_useDebounce, 2),
      actualValue = _useDebounce2[0],
      setActualValue = _useDebounce2[1];

  var onChangeEvent = React.useCallback(function (e) {
    setActualValue(e.currentTarget.value);
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
  }, [setActualValue, onChange]);
  return React.createElement("textarea", Object.assign({
    className: "arm-text-area-textarea",
    ref: ref,
    value: actualValue,
    onChange: onChangeEvent
  }, nativeProps));
});
var ThrottledTextAreaBase = React.forwardRef(function (_a, ref) {
  var milliseconds = _a.milliseconds,
      value = _a.value,
      onValueChange = _a.onValueChange,
      onChange = _a.onChange,
      nativeProps = __rest(_a, ["milliseconds", "value", "onValueChange", "onChange"]);

  var _useThrottle = useThrottle(milliseconds, value === null || value === void 0 ? void 0 : value.toString(), onValueChange),
      _useThrottle2 = _slicedToArray(_useThrottle, 2),
      actualValue = _useThrottle2[0],
      setActualValue = _useThrottle2[1];

  var onChangeEvent = React.useCallback(function (e) {
    setActualValue(e.currentTarget.value);
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
  }, [setActualValue, onChange]);
  return React.createElement("textarea", Object.assign({
    className: "arm-text-area-textarea",
    ref: ref,
    value: actualValue,
    onChange: onChangeEvent
  }, nativeProps));
});
export var TextArea = React.forwardRef(function (_a, ref) {
  var bind = _a.bind,
      onChange = _a.onChange,
      value = _a.value,
      className = _a.className,
      leftIcon = _a.leftIcon,
      rightIcon = _a.rightIcon,
      leftOverlay = _a.leftOverlay,
      rightOverlay = _a.rightOverlay,
      validationErrorMessages = _a.validationErrorMessages,
      validationMode = _a.validationMode,
      validationErrorIcon = _a.errorIcon,
      disabled = _a.disabled,
      pending = _a.pending,
      above = _a.above,
      scrollValidationErrorsIntoView = _a.scrollValidationErrorsIntoView,
      below = _a.below,
      statusPosition = _a.statusPosition,
      disableOnPending = _a.disableOnPending,
      delay = _a.delay,
      onValueChange = _a.onValueChange,
      nativeProps = __rest(_a, ["bind", "onChange", "value", "className", "leftIcon", "rightIcon", "leftOverlay", "rightOverlay", "validationErrorMessages", "validationMode", "errorIcon", "disabled", "pending", "above", "scrollValidationErrorsIntoView", "below", "statusPosition", "disableOnPending", "delay", "onValueChange"]);

  var internalRef = React.useRef(null);
  React.useImperativeHandle(ref, function () {
    return internalRef.current;
  }, [internalRef]);

  var _Form$useBindingState = Form.useBindingState(bind, {
    value: value === null || value === void 0 ? void 0 : value.toString(),
    validationErrorMessages: validationErrorMessages,
    validationMode: validationMode,
    validationErrorIcon: validationErrorIcon
  }),
      _Form$useBindingState2 = _slicedToArray(_Form$useBindingState, 3),
      boundValue = _Form$useBindingState2[0],
      setBoundValue = _Form$useBindingState2[1],
      bindConfig = _Form$useBindingState2[2];

  var onChangeEvent = React.useCallback(function (event) {
    onChange === null || onChange === void 0 ? void 0 : onChange(event);
    var currentValue = event.currentTarget.value;
    onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(currentValue);
    setBoundValue === null || setBoundValue === void 0 ? void 0 : setBoundValue(currentValue);
  }, [bind, onChange, onValueChange, setBoundValue]);
  var onValueChangeEvent = React.useCallback(function (currentValue) {
    var val = currentValue !== null && currentValue !== void 0 ? currentValue : '';
    onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(val);
    setBoundValue === null || setBoundValue === void 0 ? void 0 : setBoundValue(val);
  }, [onValueChange, setBoundValue]);
  var inputProps = {
    value: boundValue !== null && boundValue !== void 0 ? boundValue : undefined,
    disabled: disabled,
    ref: internalRef
  };
  return React.createElement(InputWrapper, {
    className: ClassNames.concat('arm-text-area', className),
    leftIcon: leftIcon,
    rightIcon: rightIcon,
    leftOverlay: leftOverlay,
    rightOverlay: rightOverlay,
    validationErrorMessages: bindConfig.validationErrorMessages,
    errorIcon: bindConfig.validationErrorIcon,
    validationMode: bindConfig.validationMode,
    disabled: disabled,
    pending: pending,
    above: above,
    disableOnPending: disableOnPending,
    statusPosition: statusPosition,
    below: below,
    onClick: function onClick() {
      var _a;

      return (_a = internalRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    },
    scrollValidationErrorsIntoView: scrollValidationErrorsIntoView
  }, (delay === null || delay === void 0 ? void 0 : delay.mode) === 'debounce' && !!delay.milliseconds && React.createElement(DebounceTextAreaBase, Object.assign({}, nativeProps, {
    milliseconds: delay.milliseconds
  }, inputProps, {
    onChange: onChange,
    onValueChange: onValueChangeEvent,
    ref: ref
  })), (delay === null || delay === void 0 ? void 0 : delay.mode) === 'throttle' && !!delay.milliseconds && React.createElement(ThrottledTextAreaBase, Object.assign({}, nativeProps, {
    milliseconds: delay.milliseconds
  }, inputProps, {
    onChange: onChange,
    onValueChange: onValueChangeEvent,
    ref: ref
  })), !(delay === null || delay === void 0 ? void 0 : delay.milliseconds) && React.createElement("textarea", Object.assign({
    className: "arm-text-area-textarea"
  }, nativeProps, {
    onChange: onChangeEvent
  }, inputProps)));
});
TextArea.defaultProps = {};